'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, Upload, Palette, Globe, Phone, Mail, Save, CheckCircle, Eye, Sun, Moon, Music2, User, Building2 } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const NEON_COLORS = [
  { label: 'Gold', value: '#FFD700' },
  { label: 'Cyan', value: '#00FFFF' },
  { label: 'Purple', value: '#BF00FF' },
  { label: 'Pink', value: '#FF006E' },
  { label: 'Green', value: '#00FF88' },
  { label: 'Orange', value: '#FF6B00' },
];

const ACCENT_COLORS = [
  { label: 'Cyan', value: '#00FFFF' },
  { label: 'Gold', value: '#FFD700' },
  { label: 'Purple', value: '#BF00FF' },
  { label: 'Pink', value: '#FF006E' },
  { label: 'Blue', value: '#0088FF' },
  { label: 'Lime', value: '#AAFF00' },
];

export default function WhiteLabelCustomize() {
  const [saved, setSaved] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [primaryColor, setPrimaryColor] = useState('#FFD700');
  const [accentColor, setAccentColor] = useState('#00FFFF');
  const [form, setForm] = useState({
    companyName: '',
    stageName: '',
    ownerName: '',
    phone: '',
    email: '',
    website: '',
    instagram: '',
    facebook: '',
    customDomain: '',
  });

  const handleChange = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const previewName = form.companyName || '[Your Business Name]';
  const previewStage = form.stageName || '[Your DJ Name]';

  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b neon-border-gold">
        <div className="max-w-screen-xl mx-auto px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link href="/white-label" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm">
              <ArrowLeft size={16} /> Back
            </Link>
            <div className="w-px h-5 bg-border" />
            <div className="flex items-center gap-2">
              <Palette size={16} className="text-primary" />
              <span className="font-semibold text-sm">Theme Customizer</span>
            </div>
          </div>
          <button
            onClick={handleSave}
            className={`flex items-center gap-2 px-5 py-2 rounded-xl font-semibold text-sm transition-all ${saved ? 'bg-green-500/20 border border-green-500/40 text-green-400' : 'gold-gradient text-black hover:opacity-90'}`}
          >
            {saved ? <><CheckCircle size={15} /> Saved!</> : <><Save size={15} /> Save Changes</>}
          </button>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* LEFT: Settings Panel */}
        <div className="lg:col-span-3 space-y-8">

          {/* Business Identity */}
          <div className="card-glass rounded-2xl p-6 neon-border-gold">
            <div className="flex items-center gap-2 mb-6">
              <Building2 size={18} className="text-primary" />
              <h2 className="font-bold text-lg">Business Identity</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { key: 'companyName', label: 'Company Name', placeholder: 'e.g. Platinum Sound Events', icon: Building2 },
                { key: 'stageName', label: 'Stage / DJ Name', placeholder: 'e.g. DJ Platinum', icon: Music2 },
                { key: 'ownerName', label: 'Owner Name', placeholder: 'e.g. Marcus Thompson', icon: User },
                { key: 'phone', label: 'Phone Number', placeholder: '(555) 000-0000', icon: Phone },
                { key: 'email', label: 'Business Email', placeholder: 'dj@yourbusiness.com', icon: Mail },
                { key: 'website', label: 'Website URL', placeholder: 'https://yourbusiness.com', icon: Globe },
              ].map(field => {
                const Icon = field.icon;
                return (
                  <div key={field.key}>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">{field.label}</label>
                    <div className="relative">
                      <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        value={form[field.key as keyof typeof form]}
                        onChange={e => handleChange(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary text-sm"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Social Links */}
          <div className="card-glass rounded-2xl p-6 border border-border">
            <h2 className="font-bold text-lg mb-5">Social Links</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { key: 'instagram', label: 'Instagram', placeholder: '@yourhandle' },
                { key: 'facebook', label: 'Facebook', placeholder: 'facebook.com/yourpage' },
              ].map(field => (
                <div key={field.key}>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">{field.label}</label>
                  <input
                    type="text"
                    value={form[field.key as keyof typeof form]}
                    onChange={e => handleChange(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    className="w-full px-4 py-2.5 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Logo Upload */}
          <div className="card-glass rounded-2xl p-6 border border-border">
            <h2 className="font-bold text-lg mb-5">Logo Upload</h2>
            <div className="border-2 border-dashed border-border rounded-xl p-10 text-center hover:border-primary/50 transition-colors cursor-pointer group">
              <Upload size={32} className="mx-auto mb-3 text-muted-foreground group-hover:text-primary transition-colors" />
              <p className="text-sm font-semibold text-foreground mb-1">Drag & drop your logo here</p>
              <p className="text-xs text-muted-foreground">PNG, JPG, SVG — max 5MB. Recommended: 400×400px</p>
              <button className="mt-4 px-4 py-2 rounded-lg border border-primary/40 text-primary text-xs font-semibold hover:bg-primary/10 transition-all">
                Browse Files
              </button>
            </div>
          </div>

          {/* Colors */}
          <div className="card-glass rounded-2xl p-6 border border-border">
            <div className="flex items-center gap-2 mb-6">
              <Palette size={18} className="text-primary" />
              <h2 className="font-bold text-lg">Brand Colors</h2>
            </div>
            <div className="space-y-6">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wider">Primary Color (Neon Accent)</label>
                <div className="flex flex-wrap gap-3">
                  {NEON_COLORS.map(c => (
                    <button
                      key={c.value}
                      onClick={() => setPrimaryColor(c.value)}
                      className="flex flex-col items-center gap-1.5 group"
                    >
                      <div
                        className="w-10 h-10 rounded-xl transition-all"
                        style={{
                          background: c.value,
                          boxShadow: primaryColor === c.value ? `0 0 16px ${c.value}80` : 'none',
                          border: primaryColor === c.value ? `2px solid ${c.value}` : '2px solid transparent',
                          transform: primaryColor === c.value ? 'scale(1.15)' : 'scale(1)',
                        }}
                      />
                      <span className="text-[10px] text-muted-foreground">{c.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wider">Accent Color</label>
                <div className="flex flex-wrap gap-3">
                  {ACCENT_COLORS.map(c => (
                    <button
                      key={c.value}
                      onClick={() => setAccentColor(c.value)}
                      className="flex flex-col items-center gap-1.5"
                    >
                      <div
                        className="w-10 h-10 rounded-xl transition-all"
                        style={{
                          background: c.value,
                          boxShadow: accentColor === c.value ? `0 0 16px ${c.value}80` : 'none',
                          border: accentColor === c.value ? `2px solid ${c.value}` : '2px solid transparent',
                          transform: accentColor === c.value ? 'scale(1.15)' : 'scale(1)',
                        }}
                      />
                      <span className="text-[10px] text-muted-foreground">{c.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Mode & Domain */}
          <div className="card-glass rounded-2xl p-6 border border-border">
            <h2 className="font-bold text-lg mb-5">Display Mode & Domain</h2>
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm">Color Mode</p>
                  <p className="text-xs text-muted-foreground">Choose your site's default appearance</p>
                </div>
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all text-sm font-semibold ${darkMode ? 'border-primary/40 text-primary bg-primary/10' : 'border-border text-muted-foreground'}`}
                >
                  {darkMode ? <Moon size={15} /> : <Sun size={15} />}
                  {darkMode ? 'Dark Mode' : 'Light Mode'}
                </button>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Custom Domain</label>
                <div className="relative">
                  <Globe size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={form.customDomain}
                    onChange={e => handleChange('customDomain', e.target.value)}
                    placeholder="https://yourdomain.com"
                    className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary text-sm"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">Point your domain's DNS to our servers. Setup guide sent after save.</p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Live Preview */}
        <div className="lg:col-span-2">
          <div className="sticky top-24">
            <div className="flex items-center gap-2 mb-4">
              <Eye size={16} className="text-accent" />
              <span className="font-semibold text-sm text-accent">Live Preview</span>
            </div>
            <div className="card-glass rounded-2xl overflow-hidden border border-border">
              {/* Mock nav */}
              <div className="px-4 py-3 border-b border-border flex items-center justify-between" style={{ background: 'rgba(18,18,26,0.95)' }}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: primaryColor }}>
                    <Music2 size={13} className="text-black" />
                  </div>
                  <div>
                    <p className="text-[9px] font-mono text-muted-foreground">{previewName}</p>
                    <p className="text-xs font-bold" style={{ color: primaryColor }}>{previewStage}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: primaryColor }} />
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: accentColor }} />
                </div>
              </div>

              {/* Mock hero */}
              <div className="p-6 text-center" style={{ background: darkMode ? '#0A0A0F' : '#F5F5F0' }}>
                <div className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: `${primaryColor}20`, border: `2px solid ${primaryColor}60` }}>
                  <Music2 size={24} style={{ color: primaryColor }} />
                </div>
                <h3 className="font-extrabold text-sm mb-1" style={{ color: darkMode ? '#F0F0F5' : '#0A0A0F' }}>{previewName}</h3>
                <p className="text-xs mb-3" style={{ color: darkMode ? '#8888AA' : '#666680' }}>Mobile DJ & Event Entertainment</p>
                <div className="flex gap-2 justify-center">
                  <div className="px-3 py-1.5 rounded-lg text-[10px] font-bold text-black" style={{ background: primaryColor }}>Book Now</div>
                  <div className="px-3 py-1.5 rounded-lg text-[10px] font-semibold border" style={{ borderColor: `${accentColor}60`, color: accentColor }}>Learn More</div>
                </div>
              </div>

              {/* Mock cards */}
              <div className="p-4 grid grid-cols-2 gap-2" style={{ background: darkMode ? '#0D0D1A' : '#EBEBF0' }}>
                {['Services', 'Pricing', 'Book', 'Contact'].map((label, i) => (
                  <div key={label} className="rounded-lg p-3 text-center" style={{ background: darkMode ? '#12121A' : '#FFFFFF', border: `1px solid ${i % 2 === 0 ? primaryColor : accentColor}30` }}>
                    <p className="text-[10px] font-semibold" style={{ color: i % 2 === 0 ? primaryColor : accentColor }}>{label}</p>
                  </div>
                ))}
              </div>

              <div className="px-4 py-3 border-t border-border text-center">
                <p className="text-[10px] text-muted-foreground">Powered by EventPro Suite</p>
              </div>
            </div>

            <div className="mt-4 p-4 rounded-xl bg-muted/50 border border-border">
              <p className="text-xs text-muted-foreground leading-relaxed">
                <span className="text-primary font-semibold">Live preview</span> updates as you type. Changes apply to your public site, client portal, vendor portal, and all automated emails.
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
